import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiMenu, FiX, FiChevronRight } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navLinks = [
        { name: 'Philosophy', path: '/' },
        { name: 'Digital Lab', path: '/study-materials' },
        { name: 'Schedules', path: '/features' },
        { name: 'Research', path: '/about' },
    ];

    return (
        <nav className={`fixed top-0 left-0 w-full z-[1000] transition-all duration-500 py-8 ${scrolled ? 'bg-[#0a0a0acc]/70 backdrop-blur-3xl py-5 border-b border-white/[0.05] shadow-2xl' : 'bg-transparent'
            }`}>
            <div className="max-w-7xl mx-auto px-6 md:px-12 flex justify-between items-center">
                <Link to="/" className="relative group">
                    <div className="text-xl md:text-2xl font-black text-white tracking-tighter transition-all duration-500 group-hover:tracking-normal">
                        MANOJ <span className="text-blue-500">SAMPATH</span>
                    </div>
                    <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600 transition-all duration-500 group-hover:w-full"></div>
                </Link>

                {/* Desktop Links */}
                <div className="hidden lg:flex items-center gap-12">
                    {navLinks.map((link) => (
                        <Link
                            key={link.name}
                            to={link.path}
                            className="text-[10px] font-black uppercase tracking-[0.3em] text-neutral-400 hover:text-white transition-all relative group"
                        >
                            {link.name}
                            <span className="absolute -bottom-2 translate-y-2 left-0 w-full h-[1px] bg-blue-500 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300"></span>
                        </Link>
                    ))}
                    <Link to="/verify">
                        <motion.button
                            whileHover={{ scale: 1.05, boxShadow: '0 0 25px rgba(59, 130, 246, 0.4)' }}
                            whileTap={{ scale: 0.95 }}
                            className="bg-blue-600 text-white px-8 py-3.5 rounded-[1.2rem] font-black text-[10px] uppercase tracking-widest transition-all shadow-xl shadow-blue-500/10"
                        >
                            Secure Portal
                        </motion.button>
                    </Link>
                </div>

                {/* Mobile Menu Button */}
                <button
                    className="lg:hidden w-12 h-12 rounded-2xl bg-white/[0.03] border border-white/[0.08] flex items-center justify-center text-white text-xl transition-all active:scale-90"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    {isOpen ? <FiX /> : <FiMenu />}
                </button>
            </div>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, x: '100%' }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="fixed inset-0 top-0 left-0 w-full h-[100vh] bg-[#0a0a0a] z-[999] lg:hidden flex flex-col p-12 pt-32 gap-10"
                    >
                        <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-br from-blue-600/[0.05] to-transparent pointer-events-none"></div>

                        {navLinks.map((link, i) => (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.1 }}
                                key={link.name}
                            >
                                <Link
                                    to={link.path}
                                    className="text-4xl md:text-6xl font-black text-white tracking-tighter flex items-center justify-between group"
                                    onClick={() => setIsOpen(false)}
                                >
                                    {link.name}
                                    <FiChevronRight className="text-xl text-blue-500 opacity-0 -translate-x-10 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                                </Link>
                            </motion.div>
                        ))}

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                            className="mt-auto"
                        >
                            <Link to="/verify" onClick={() => setIsOpen(false)}>
                                <button className="w-full bg-blue-600 text-white py-6 rounded-[2rem] font-black text-sm uppercase tracking-widest">
                                    Secure Portal Login
                                </button>
                            </Link>
                            <p className="text-center mt-8 text-neutral-600 text-[10px] font-black uppercase tracking-widest">
                                Copyright © {new Date().getFullYear()} Manoj Sampath
                            </p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
};

export default Navbar;
